import React from 'react';

import { SpendTypeEnum } from 'components/Classification';
import { ProductAllocation } from './ProductAllocation';
import { Subheader } from 'components/SpendAllocation/Subheader';
import { ErrorPage } from 'views/Error';
import { ReuseAllocationModal } from 'components/SpendAllocation/ReuseAllocationModal';
import { NavigateUnsavedModal } from 'components/SpendAllocation/NavigateUnsavedModal';
import { DateSwitchModal } from 'components/SpendAllocation/DateSwitchModal';
import { LoaderWrapper, NavigationPromptWrapper } from '../styles';
import { ISettingsResponse, ICurrencyFormat } from 'utils/types';
import NavigationPrompt from 'react-router-navigation-prompt';
import { getQueryDatesFromPeriod } from 'context/QueryParams';
import {
  getProductNonAppAllocations,
  getProductAppAllocations,
  getApplicationSpend,
  getProducts,
  updateNonAppProductAllocations,
  updateAppProductAllocations,
} from 'utils/api';
import {
  ApLoaderDefault,
  ApLoaderText,
  IOption,
} from '@alixpartners/ui-components';
import {
  isAllocationItemValid,
  contributionField,
  contributionQueryParam,
} from 'components/SpendAllocation/utils';
import {
  IDimensionGroup,
  IAllocatedYear,
  IAllocatedItem,
  IAllocation,
  DimensionTypeEnum,
} from 'components/SpendAllocation/types';
import {
  createPeriodsFromRange,
  getPeriodStr,
} from 'views/Settings/settings.utils';
import isEqual from 'lodash/isEqual';

interface IState {
  selectedDate: IOption;
  dateToChange: IOption | null;
  dimensions: IDimensionGroup[];
  applications: IDimensionGroup[];
  products: IAllocation[];
  allocationsByYears: IAllocatedYear[];
  allocations: IAllocatedItem[];
  error: any;
  isLoading: boolean;
  isSaveActive: boolean;
  isReuseModalOpen: boolean;
  isSaving: boolean;
  isDimensionsLoading: boolean;
  isDateSwitchModalOpen: boolean;
  isDateLoading: boolean;
}

interface IProps {
  settings: ISettingsResponse;
  permissions: string[];
  format: ICurrencyFormat;
  spendType: SpendTypeEnum;
  availableYears: number[];
}

const BACK_URL = '/settings/data/products';

export class ProductAllocationController extends React.Component<
  IProps,
  IState
> {
  state = {
    selectedDate: this.getInitialSelectedDate(),
    error: null as IState['error'],
    allocationsByYears: [] as IState['allocationsByYears'],
    allocations: [] as IState['allocations'],
    dimensions: [] as IState['dimensions'],
    applications: [] as IState['applications'],
    products: [],
    isLoading: false,
    isSaveActive: false,
    isDimensionsLoading: false,
    isSaving: false,
    dateToChange: null as IState['dateToChange'],
    isReuseModalOpen: false,
    isDateSwitchModalOpen: false,
    isDateLoading: false,
  };

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps: IProps, prevState: IState) {
    const { allocationsByYears, selectedDate, allocations } = this.state;

    if (
      selectedDate &&
      prevState.selectedDate &&
      selectedDate.value !== prevState.selectedDate.value
    ) {
      this.setState(
        {
          allocations: this.getAllocationsBySelectedDate(),
        },
        this.fetchDimensions
      );
    }

    if (!isEqual(allocations, prevState.allocations)) {
      this.validateSaveButton();
    }

    if (allocationsByYears !== prevState.allocationsByYears) {
      this.setState({
        allocations: this.getAllocationsBySelectedDate(),
      });
    }
  }

  fetchData = () => {
    this.setState({ error: null, isLoading: true });

    return Promise.all([
      this.fetchAllocations(),
      this.fetchProducts(),
      this.fetchDimensions(),
    ])
      .then(() => {
        this.setState({
          isLoading: false,
        });
      })
      .catch((error) => {
        this.setState({ error, isLoading: false });
      });
  };

  getSelectedDateParams = () => {
    const { settings } = this.props;
    const { selectedDate } = this.state;
    const currentPeriod = {
      ...settings.selectedYear,
      startYear: Number(selectedDate.value),
      endYear: Number(selectedDate.value) + 1,
    };

    return getQueryDatesFromPeriod(currentPeriod);
  };

  getAllocationsBySelectedDate = () => {
    const { allocationsByYears, selectedDate } = this.state;

    return (
      allocationsByYears.find(
        (item: IAllocatedYear) => String(item.year) === selectedDate.value
      )?.allocations || []
    );
  };

  fetchAllocations = () => {
    const { spendType } = this.props;
    const params = {
      contribution: contributionField[spendType],
    };

    return Promise.all([
      getProductAppAllocations(params),
      getProductNonAppAllocations(params),
    ]).then(([appAllocations, nonAppAllocations]) => {
      const appYears = appAllocations.map((item) => item.year) || [];
      const nonAppYears = nonAppAllocations.map((item) => item.year) || [];
      const combinedYears = [...new Set([...appYears, ...nonAppYears])];
      const combinedAllocations = combinedYears.map((year) => {
        const appYear = appAllocations.find((item) => item.year === year);
        const nonAppYear = nonAppAllocations.find((item) => item.year === year);

        return {
          year,
          allocations: [
            ...(appYear ? appYear.allocations : []),
            ...(nonAppYear ? nonAppYear.allocations : []),
          ],
        };
      });

      this.setState({
        allocationsByYears: combinedAllocations || [],
      });
    });
  };

  fetchDimensions = () => {
    const { spendType } = this.props;

    this.setState({ isDimensionsLoading: true });
    const params = {
      ...this.getSelectedDateParams(),
      contributions: contributionQueryParam[spendType],
    };

    return getApplicationSpend(params).then((resp) => {
      this.setState({
        dimensions: resp.appSpend.nonApplicationsSpend || [],
        applications: resp.appSpend.applicationsSpend.map((item) => ({
          id: String(item.applicationId),
          name: item.applicationName,
          spend: item.spend,
        })),
        isDimensionsLoading: false,
      });
    });
  };

  fetchProducts = () => {
    return getProducts(this.getSelectedDateParams()).then((products) => {
      this.setState({
        products,
      });
    });
  };

  getInitialSelectedDate() {
    const { settings, availableYears } = this.props;
    const { selectedYear } = settings;
    const options = this.getDateOptions();
    const isCurrentAvailable = availableYears.includes(selectedYear.startYear);

    const selectedOption = options.find((item) => {
      const condition = isCurrentAvailable
        ? item.value === String(selectedYear.startYear)
        : !item.disabled;

      return condition;
    });

    return selectedOption
      ? selectedOption
      : options[0] || { label: 'No data', value: 0 };
  }

  getDateOptions() {
    const { settings, availableYears } = this.props;
    const { availableRange, selectedYear } = settings;

    const years = createPeriodsFromRange(
      availableRange,
      selectedYear.startPeriod
    );

    return years.reverse().map((item) => {
      return {
        label: getPeriodStr(item),
        value: String(item.startYear),
        disabled: !availableYears.includes(item.startYear),
      };
    });
  }

  saveAllocation = () => {
    const { allocations, selectedDate } = this.state;
    const { spendType } = this.props;

    const appAllocations = allocations
      .filter((item) => !item.type)
      .map((item) => ({ ...item, type: DimensionTypeEnum.Application }));
    const nonAppAllocations = allocations.filter((item) => item.type);

    this.setState({ isSaving: true });

    return Promise.all([
      updateAppProductAllocations(
        [{ year: Number(selectedDate.value), allocations: appAllocations }],
        contributionField[spendType]
      ),
      updateNonAppProductAllocations(
        [{ year: Number(selectedDate.value), allocations: nonAppAllocations }],
        contributionField[spendType]
      ),
    ]).then(() => {
      this.setState({ isSaving: false, isSaveActive: false });
      this.fetchAllocations();
    });
  };

  validateSaveButton = () => {
    this.setState({
      isSaveActive: this.hasAllocationsChanged() && this.isAllocationsValid(),
    });
  };

  isAllocationsValid = () => {
    const { allocations } = this.state;

    return !allocations.some((item) => !isAllocationItemValid(item.items));
  };

  hasAllocationsChanged = () => {
    const { allocations } = this.state;
    const originalAllocations = this.getAllocationsBySelectedDate();

    return !isEqual(originalAllocations, allocations);
  };

  getAllocatedYears = () => {
    const { allocationsByYears } = this.state;

    return allocationsByYears.map((item) => String(item.year));
  };

  handleDateChange = (option: IOption) => {
    if (!this.hasAllocationsChanged()) {
      this.setState({ selectedDate: option });

      return;
    }

    this.openDateSwitchModal();
    this.setState({ dateToChange: option });
  };

  changeDateFromModal = () => {
    const { dateToChange } = this.state;

    if (!dateToChange) {
      return;
    }

    this.setState({
      selectedDate: dateToChange,
    });
    this.closeDateSwitchModal();
  };

  saveAndChangeDateFromModal = () => {
    this.saveAllocation();
    this.changeDateFromModal();
  };

  openDateSwitchModal = () => {
    this.setState({
      isDateSwitchModalOpen: true,
    });
  };

  closeDateSwitchModal = () => {
    this.setState({
      isDateSwitchModalOpen: false,
      dateToChange: null,
    });
  };

  reuseAllocation = (year: number) => {
    const { allocationsByYears, selectedDate, dimensions, applications } =
      this.state;

    const reusedAllocations =
      allocationsByYears.find((item: IAllocatedYear) => item.year === year)
        ?.allocations || [];

    this.setState({
      allocations: reusedAllocations
        .filter((item) => {
          if (!item.type) {
            return !!applications.find((app) => app.id === item.dimensionId);
          }

          return (
            item.type === dimensions[0]?.type &&
            !!dimensions.find(
              (category) =>
                category.id === item.dimensionId ||
                !!category.children?.find(
                  (child) => child.id === item.dimensionId
                )
            )
          );
        })
        .map((allocation) => ({
          ...allocation,
          year: Number(selectedDate.value),
        })),
    });
    this.closeReuseAllocationModal();
  };

  getReuseAllocationOptions = () => {
    const { selectedDate } = this.state;
    const dateOptions = this.getDateOptions();
    const allocatedYears = this.getAllocatedYears();
    const inheritAllocations = allocatedYears
      ? allocatedYears.filter(
          (item) =>
            item !== selectedDate.value &&
            dateOptions.find((dateOption) => dateOption.value === item)
        )
      : [];

    if (!inheritAllocations || inheritAllocations.length === 0) {
      return null;
    }

    const options = dateOptions.filter(
      (item) => item.value !== selectedDate.value
    );

    return options.map((item) => ({
      ...item,
      isAllocated: allocatedYears.includes(item.value),
    }));
  };

  changeAllocation = (allocation: IAllocatedItem) => {
    const { allocations } = this.state;
    const changedAllocationIndex = allocations.findIndex(
      (item) => item.dimensionId === allocation.dimensionId
    );
    let updatedAllocations = [];

    if (allocation.items.length > 0) {
      if (changedAllocationIndex !== -1) {
        updatedAllocations = [
          ...allocations.filter((item, index) => {
            return index !== changedAllocationIndex;
          }),
          allocation,
        ];
      } else {
        updatedAllocations = [...allocations, allocation];
      }
    } else {
      updatedAllocations = allocations.filter((item, index) => {
        return index !== changedAllocationIndex;
      });
    }

    this.setState({
      allocations: updatedAllocations,
    });
  };

  openReuseAllocationModal = () => {
    this.setState({ isReuseModalOpen: true });
  };

  closeReuseAllocationModal = () => {
    this.setState({ isReuseModalOpen: false });
  };

  render() {
    const { spendType, permissions, format } = this.props;
    const {
      selectedDate,
      error,
      dimensions,
      isLoading,
      isSaveActive,
      applications,
      products,
      allocations,
      isReuseModalOpen,
      isDateSwitchModalOpen,
      isSaving,
      isDimensionsLoading,
    } = this.state;

    if (!selectedDate) {
      return (
        <LoaderWrapper>
          <ApLoaderDefault loaderText={() => <ApLoaderText />} />
        </LoaderWrapper>
      );
    }

    return (
      <React.Fragment>
        <NavigationPromptWrapper>
          <NavigationPrompt
            disableNative
            when={(currentLocation, nextLocation) =>
              currentLocation.pathname !== nextLocation?.pathname &&
              this.hasAllocationsChanged()
            }
          >
            {({ onConfirm, onCancel }) => (
              <NavigateUnsavedModal
                onSave={() => {
                  this.saveAllocation().then(onConfirm);
                }}
                onClose={onCancel}
                onConfirm={onConfirm}
                isInvalid={!this.isAllocationsValid()}
              />
            )}
          </NavigationPrompt>
        </NavigationPromptWrapper>
        <Subheader
          dateOptions={this.getDateOptions()}
          selectedDate={selectedDate}
          onSave={this.saveAllocation}
          onDateChange={this.handleDateChange}
          spendType={spendType}
          isSaveActive={isSaveActive && !isSaving}
          backUrl={BACK_URL}
          title="product"
        />
        {error ? (
          <ErrorPage
            title="Spend product allocation"
            description={
              error.message ? error.message.title || error.message : ''
            }
            onClick={this.fetchData}
          />
        ) : isLoading ? (
          <LoaderWrapper>
            <ApLoaderDefault loaderText={() => <ApLoaderText />} />
          </LoaderWrapper>
        ) : (
          <ProductAllocation
            spendType={spendType}
            permissions={permissions}
            format={format}
            dimensions={dimensions}
            allocations={allocations}
            products={products}
            applications={applications}
            onReuseAllocation={this.openReuseAllocationModal}
            onAllocationChange={this.changeAllocation}
            selectedYear={Number(selectedDate.value)}
            isDimensionsLoading={isDimensionsLoading}
          />
        )}
        {isReuseModalOpen && (
          <ReuseAllocationModal
            onSubmit={this.reuseAllocation}
            onClose={this.closeReuseAllocationModal}
            isOpen={isReuseModalOpen}
            options={this.getReuseAllocationOptions()}
            body="You can reuse the product spend allocation from a different time period by selecting from the list below. We will allocate spend using the same applications and non-application categories and then reuse those percentages, where possible."
          />
        )}
        {isDateSwitchModalOpen && (
          <DateSwitchModal
            onSave={this.saveAndChangeDateFromModal}
            onClose={this.closeDateSwitchModal}
            onSwitch={this.changeDateFromModal}
            isOpen={isDateSwitchModalOpen}
            isInvalid={!this.isAllocationsValid()}
          />
        )}
      </React.Fragment>
    );
  }
}
